import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Upload, Check, ArrowRight, ArrowLeft, Sparkles, RotateCcw, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { SizeChart } from "../components/SizeChart";

const G = "https://static.prod-images.emergentagent.com/jobs/166ceceb-055c-4119-89c1-400addb91182/images/";
const GUIDES = {
  women: {
    front: `${G}72188819b494c99847b54adad03efce669c9cc6f3536313f487507c180498c68.jpeg`,
    side: `${G}118227118d6f6457921e2f23018b6a9b4b5be8551d54e1876b3b67c57b826c39.jpeg`,
    back: `${G}d6b43b76521c095da5653bed47a7bde2c02c1a862ee88c2452b898954149b3da.jpeg`,
  },
  men: {
    front: `${G}82c61a66cac0be2695645b55dfd7f4c8c11bb671ff41ded1fc854a8f7aade2d2.jpeg`,
    side: `${G}8327f8212948763b78371c0321116bbccb910fcb0bb5370022f3aed087dcd294.jpeg`,
    back: `${G}5b40d7f4ec9fa70feb07559b46211bb169c03a76f4061063ec4990208e9ac4a4.jpeg`,
  },
};
const VIEWS = [
  { key: "front", label: "Front", hint: "Face the camera, arms slightly away from your body." },
  { key: "side", label: "Side", hint: "Turn 90°, stand naturally, arms relaxed." },
  { key: "back", label: "Back", hint: "Turn away from the camera, stand tall." },
];

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default function FitStudio() {
  const { fitProfile, setFitProfile } = useShop();
  const [step, setStep] = useState(fitProfile ? "result" : "intro");
  const [gender, setGender] = useState(fitProfile?.gender || "women");
  const [height, setHeight] = useState(fitProfile?.height_cm || "");
  const [images, setImages] = useState({ front: null, side: null, back: null });
  const [result, setResult] = useState(fitProfile || null);
  const [error, setError] = useState("");
  const [dragKey, setDragKey] = useState(null);
  const fileRefs = { front: useRef(), side: useRef(), back: useRef() };

  const onPick = async (key, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    const b64 = await fileToBase64(file);
    setImages((p) => ({ ...p, [key]: b64 }));
  };

  const allUploaded = images.front && images.side && images.back;

  const analyze = async () => {
    if (!height || Number(height) < 120 || Number(height) > 230) {
      toast.error("Please enter a valid height in cm");
      setStep("intro");
      return;
    }
    setStep("analyzing");
    setError("");
    try {
      const { data } = await api.post("/measurements/analyze", {
        gender,
        height_cm: Number(height),
        front_image: images.front,
        side_image: images.side,
        back_image: images.back,
      });
      setResult(data);
      setFitProfile(data);
      setStep("result");
      toast.success("Your fit profile is ready");
    } catch (e) {
      setError("We couldn't read your photos. Please try again with clearer, well-lit images.");
      setStep("upload");
      toast.error("Fit analysis failed — please retry");
    }
  };

  const restart = () => {
    setImages({ front: null, side: null, back: null });
    setResult(null);
    setFitProfile(null);
    localStorage.removeItem("shiokoi_fit");
    setStep("intro");
  };

  return (
    <div data-testid="fit-studio-page" className="pt-20 min-h-screen bg-sand">
      <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-16">
        <div className="text-center mb-12">
          <div className="overline text-sunkissed mb-3">The Fit Studio</div>
          <h1 className="font-serif text-4xl md:text-6xl font-light">Find your perfect fit</h1>
          <p className="text-charcoal/60 mt-4 max-w-md mx-auto">
            Three photos in fitted underwear. Our fit intelligence maps your body so every piece is cut only for you.
          </p>
        </div>

        {/* INTRO */}
        {step === "intro" && (
          <div className="max-w-md mx-auto fade-up" data-testid="fit-intro">
            <div className="mb-8">
              <div className="overline mb-3">I am shopping for</div>
              <div className="grid grid-cols-2 gap-3">
                {["women", "men"].map((g) => (
                  <button
                    key={g}
                    data-testid={`gender-${g}`}
                    onClick={() => setGender(g)}
                    className={`py-4 border capitalize overline transition-colors ${
                      gender === g ? "bg-ocean text-sand border-ocean" : "border-charcoal/25 hover:border-charcoal"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-10">
              <div className="overline mb-3">Your height (cm) — used to calibrate</div>
              <input
                data-testid="height-input"
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="e.g. 172"
                className="w-full bg-transparent border-b border-charcoal/30 py-3 text-2xl font-serif focus:outline-none focus:border-charcoal"
              />
            </div>
            <div className="flex items-start gap-3 text-xs text-charcoal/55 mb-8 p-4 bg-linen/60">
              <ShieldCheck size={18} className="shrink-0 mt-0.5" />
              Your photos are used only to estimate your measurements and are never shared.
            </div>
            <button
              data-testid="intro-continue"
              disabled={!height}
              onClick={() => setStep("upload")}
              className="w-full bg-ocean text-sand py-4 overline hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              Continue to photos
            </button>
          </div>
        )}

        {/* UPLOAD */}
        {step === "upload" && (
          <div className="fade-up" data-testid="fit-upload">
            {error && <div className="text-center text-destructive text-sm mb-6" data-testid="fit-error">{error}</div>}
            <div className="grid md:grid-cols-3 gap-5">
              {VIEWS.map((v) => (
                <div key={v.key} className="text-center">
                  <input
                    ref={fileRefs[v.key]}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    data-testid={`file-input-${v.key}`}
                    onChange={(e) => onPick(v.key, e.target.files[0])}
                  />
                  <button
                    data-testid={`dropzone-${v.key}`}
                    onClick={() => fileRefs[v.key].current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragKey(v.key); }}
                    onDragLeave={() => setDragKey(null)}
                    onDrop={(e) => { e.preventDefault(); setDragKey(null); onPick(v.key, e.dataTransfer.files[0]); }}
                    className={`relative w-full aspect-[3/4] border overflow-hidden rounded-2xl flex flex-col items-center justify-center transition-colors ${
                      dragKey === v.key
                        ? "border-sunkissed bg-linen/60 ring-2 ring-sunkissed/40"
                        : images[v.key] ? "border-charcoal" : "border-dashed border-charcoal/30 hover:border-charcoal hover:bg-linen/40"
                    }`}
                  >
                    {images[v.key] ? (
                      <>
                        <img src={images[v.key]} alt={v.label} className="absolute inset-0 w-full h-full object-cover" />
                        <span className="absolute top-3 right-3 bg-sunkissed text-white rounded-full p-1">
                          <Check size={14} />
                        </span>
                      </>
                    ) : (
                      <>
                        <img src={(GUIDES[gender] || GUIDES.women)[v.key]} alt={`${v.label} guide`} className="absolute inset-0 w-full h-full object-contain opacity-70 p-4" />
                        <div className="relative flex flex-col items-center bg-sand/85 backdrop-blur-sm px-4 py-3 rounded-xl">
                          <Camera size={22} strokeWidth={1.3} className="text-charcoal/80 mb-1.5" />
                          <span className="overline text-charcoal/80">Add {v.label}</span>
                          <span className="text-[10px] text-charcoal/50 mt-1">Drag & drop or click</span>
                        </div>
                      </>
                    )}
                  </button>
                  <p className="text-xs text-charcoal/50 mt-3 leading-relaxed px-1">{v.hint}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center mt-10">
              <button data-testid="back-to-intro" onClick={() => setStep("intro")} className="overline flex items-center gap-2 hover:opacity-60 transition-opacity">
                <ArrowLeft size={16} /> Back
              </button>
              <button
                data-testid="analyze-btn"
                disabled={!allUploaded}
                onClick={analyze}
                className="inline-flex items-center gap-3 bg-ocean text-sand px-8 py-4 overline hover:opacity-90 transition-opacity disabled:opacity-40"
              >
                <Sparkles size={16} /> Analyse my fit
              </button>
            </div>
          </div>
        )}

        {/* ANALYZING */}
        {step === "analyzing" && (
          <div className="text-center py-24 fade-up" data-testid="fit-analyzing">
            <div className="w-16 h-16 mx-auto border-2 border-charcoal/15 border-t-ocean rounded-full animate-spin mb-8" />
            <div className="font-serif text-2xl">Reading your body…</div>
            <p className="text-charcoal/55 mt-2">Mapping your proportions from three angles.</p>
          </div>
        )}

        {/* RESULT */}
        {step === "result" && result && (
          <div className="fade-up" data-testid="fit-result">
            <div className="text-center mb-10">
              <div className="w-14 h-14 mx-auto rounded-full bg-ocean text-sand flex items-center justify-center mb-5">
                <Check size={26} />
              </div>
              <h2 className="font-serif text-3xl">Your SHIOKOI size is {result.recommended_size}</h2>
              <p className="text-charcoal/60 mt-2 max-w-md mx-auto">{result.fit_notes}</p>
              <span className="inline-block overline text-charcoal/60 mt-3">Confidence: {result.confidence}</span>
            </div>

            <div className="bg-white border border-charcoal/10 rounded-2xl p-8 max-w-lg mx-auto">
              <div className="overline mb-5">Your measurements</div>
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                {Object.entries(result.measurements_cm).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-charcoal/10 pb-2" data-testid={`measure-${k}`}>
                    <span className="text-sm text-charcoal/70 capitalize">{k.replace(/_/g, " ")}</span>
                    <span className="text-sm font-medium">{typeof v === "number" ? `${v} cm` : v}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 max-w-lg mx-auto">
              <div className="overline mb-3 text-center">Compare with our size chart</div>
              <SizeChart gender={result.gender} highlight={result.recommended_size} />
            </div>

            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <Link
                to={result.gender === "men" ? "/men" : "/women"}
                data-testid="result-shop"
                className="inline-flex items-center gap-3 bg-ocean text-sand px-8 py-4 overline hover:opacity-90 transition-opacity"
              >
                Shop your tailored pieces <ArrowRight size={16} />
              </Link>
              <button data-testid="result-restart" onClick={restart} className="inline-flex items-center gap-2 border border-charcoal/25 px-8 py-4 overline hover:border-charcoal transition-colors">
                <RotateCcw size={15} /> Redo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
